import React from 'react';
import { Box } from 'rebass';
import Slider from 'react-slick';
import { Controller, Scene } from 'react-scrollmagic';

import TwoToneCarouselSlide from '../../components/TwoToneCarouselSlide/TwoToneCarouselSlide';
import NarrowContainer from '../../components/NarrowContainer/NarrowContainer';
import CarouselChevron from '../../components/CarouselChevron/CarouselChevron';
import Typography from '../../components/Typography/Typography';

import colourMatching from '../../utils/colourMatching';
import breakpointConverter from '../../utils/breakpointConverter';
import { theme } from '../../styles/theme';
import CarouselStyles from '../../styles/CarouselStyles';

import TwoToneBkg from './TwoToneBkg';
import isFadeEnabled from '../../utils/isFadeEnabled';
import { TwoToneCarouselSectionStoryblok } from '../../types/storyblok';

const TwoToneCarouselSection = ({
  slides,
  backgroundLeft,
  backgroundRight,
}: TwoToneCarouselSectionStoryblok) => {
  const slider = React.useRef();
  const nextSlide = () => slider.current.slickNext();
  const prevSlide = () => slider.current.slickPrev();
  const sliderSettings = {
    dots: false,
    infinite: true,
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: breakpointConverter(theme.breakpoint.md),
        settings: {
          dots: true,
        },
      },
    ],
  };

  return (
    <TwoToneBkg
      forwardedAs="section"
      backgroundLeft={colourMatching(backgroundLeft)}
      backgroundRight={colourMatching(backgroundRight)}
      px={[0, , 4]}
      pt={[0, , 10]}
      pb={[8, 9, 10]}
      sx={{ position: 'relative' }}
    >
      <Controller globalSceneOptions={{ triggerHook: 'onEnter' }}>
        <Scene classToggle="full-opacity">
          <NarrowContainer className={isFadeEnabled() ? 'zero-opacity' : ''}>
            <Box
              width={1}
              px={[, , 5]}
              sx={{
                position: 'relative',
                '&& .slick-track': {
                  alignItems: ['flex-start', , 'center'],
                },
              }}
            >
              <CarouselStyles colourScheme={backgroundRight}>
                <Slider ref={slider} {...sliderSettings}>
                  {slides.length > 0 ? (
                    slides.map((slide) => {
                      return (
                        <TwoToneCarouselSlide
                          key={slide._uid}
                          title={slide.title}
                          richText={slide.richText}
                          buttonText={slide.buttonText}
                          buttonUrl={slide.buttonUrl}
                          logo={slide.logo}
                          image={slide.image}
                        />
                      );
                    })
                  ) : (
                    <Typography textAlign="center">No slides added</Typography>
                  )}
                </Slider>
              </CarouselStyles>

              {slides.length > 1 && (
                <Box>
                  <CarouselChevron
                    left
                    onClick={prevSlide}
                    p={3}
                    mx={2}
                    colourScheme={backgroundLeft}
                  />
                  <CarouselChevron
                    right
                    onClick={nextSlide}
                    p={3}
                    mx={2}
                    colourScheme={backgroundRight}
                  />
                </Box>
              )}
            </Box>
          </NarrowContainer>
        </Scene>
      </Controller>
    </TwoToneBkg>
  );
};

export default TwoToneCarouselSection;
